import {
  CollectionActions,
  CollectionStateSlice,
  mergeDocuments,
  mergeWebsocketDocuments,
  pluckDocumentFromRawStoreCollectionState,
  Reference,
  transformIntoDocuments,
  updateWebsocketDocuments,
} from '@freelancer/datastore/core';
import { isDefined } from '@freelancer/utils';
import { EnterpriseApi } from 'api-typings/common/common';
import {
  OnlineOfflineCollection,
  _transformOnlineOfflineStatusType,
} from '../online-offline';
import { UsersSelfCollection } from '../users-self';
import { transformUser } from './users.transformers';
import { UsersCollection } from './users.types';

function isArrayOfIds(
  ids: ReadonlyArray<EnterpriseApi | undefined> | undefined,
): ids is ReadonlyArray<EnterpriseApi> {
  return ids !== undefined && !!ids.filter(isDefined);
}

export function usersReducer(
  state: CollectionStateSlice<UsersCollection> = {},
  action:
    | CollectionActions<UsersCollection>
    | CollectionActions<OnlineOfflineCollection>
    | CollectionActions<UsersSelfCollection>,
) {
  switch (action.type) {
    case 'API_FETCH_SUCCESS': {
      if (action.payload.type === 'users') {
        const { result, ref, order } = action.payload;
        if (result.users) {
          return mergeDocuments<UsersCollection>(
            state,
            transformIntoDocuments(result.users, transformUser),
            order,
            ref,
          );
        }
        return state;
      }

      return state;
    }

    case 'API_UPDATE_SUCCESS': {
      if (action.payload.type === 'usersSelf') {
        const { originalDocument, ref, delta } = action.payload;

        const { enterpriseIds } = delta;
        if (originalDocument.id && isArrayOfIds(enterpriseIds)) {
          return updateWebsocketDocuments<UsersCollection>(
            state,
            [originalDocument.id],
            user => ({
              ...user,
              ...{
                enterpriseIds,
              },
            }),
            { path: { collection: 'users', authUid: ref.path.authUid } },
          );
        }
      }

      return state;
    }

    case 'WS_MESSAGE': {
      const ref: Reference<UsersCollection> = {
        path: { collection: 'users', authUid: action.payload.toUserId },
      };
      if (
        action.payload.parent_type === 'onlineoffline' &&
        action.payload.type === 'statusget'
      ) {
        const { data } = action.payload;
        return updateWebsocketDocuments<UsersCollection>(
          state,
          Object.keys(data),
          user => {
            // replace status and remove timestamp if it's different
            const status = _transformOnlineOfflineStatusType(data[user.id]);
            const lastUpdatedTimestamp =
              user.onlineOfflineStatus &&
              user.onlineOfflineStatus.status === status
                ? user.onlineOfflineStatus.lastUpdatedTimestamp
                : undefined;
            return {
              ...user,
              onlineOfflineStatus: {
                status,
                lastUpdatedTimestamp,
              },
            };
          },
          ref,
        );
      }
      if (
        action.payload.parent_type === 'onlineoffline' &&
        action.payload.type === 'statuschange'
      ) {
        const { data } = action.payload;
        const storeUser = pluckDocumentFromRawStoreCollectionState(
          state,
          ref.path,
          data.user.toString(),
        );
        if (storeUser) {
          return mergeWebsocketDocuments<UsersCollection>(
            state,
            transformIntoDocuments([storeUser], user => ({
              ...user,
              onlineOfflineStatus: {
                status: _transformOnlineOfflineStatusType(data.to),
                lastUpdatedTimestamp: Date.now(),
              },
            })),
            ref,
          );
        }
      }
      return state;
    }

    default:
      return state;
  }
}
